/**
 * Coordinates onboarding so at most one guidance surface is visible at a time:
 *   active tour  >  first-run shell tour (auto_start)  >  What's New  >  tour offer.
 * Progress/acknowledgement is persisted by the backend at the tour's/notice's own scope.
 * Someone who finished or skipped an older version of a tour is offered only its new steps;
 * replaying from Profile or What's New always shows the whole tour.
 */
import { Compass, X } from "lucide-react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router";

import { useAcknowledgeNotices, useNotices, useTourProgress, useTours } from "@/api/queries/guidance";
import type { Tour, TourStep } from "@/api/types";
import { Button } from "@/components/ui";

import { TourOverlay } from "./TourOverlay";
import { WhatsNewDialog } from "./WhatsNewDialog";

type GuidanceApi = {
  tours: Tour[];
  activeTourKey: string | null;
  startTour: (key: string) => void;
  replayAll: () => void;
  openWhatsNew: () => void;
};

const GuidanceContext = createContext<GuidanceApi>({
  tours: [],
  activeTourKey: null,
  startTour: () => {},
  replayAll: () => {},
  openWhatsNew: () => {},
});

export const useGuidance = () => useContext(GuidanceContext);

/** Steps added since the version this person last finished or skipped (none if they never did). */
export function newSteps(tour: Tour): TourStep[] {
  const seen = tour.seen_version;
  return seen == null ? [] : tour.steps.filter((step) => step.since > seen);
}

const offeredThisSession = new Set<string>();

/**
 * A dialog (or popover) the person is working in. Automatic guidance waits until it closes: opening
 * over it would take focus from what they're typing. Radix unmounts closed dialogs, so presence = open.
 */
const dialogOpen = () => document.querySelector('[role="dialog"], [role="alertdialog"]') !== null;

export function GuidanceProvider({
  slug,
  sectionFromPath,
  passive = false,
  children,
}: {
  slug: string;
  sectionFromPath: (path: string) => string | null;
  /** No automatic tours/notices/offers (e.g. while impersonating); manual replay still works. */
  passive?: boolean;
  children: ReactNode;
}) {
  const toursQuery = useTours(slug);
  const unreadQuery = useNotices(slug, true);
  const progress = useTourProgress(slug);
  // `useMutation` returns a new object each render; `mutate` itself is stable.
  const recordProgress = progress.mutate;
  const acknowledge = useAcknowledgeNotices(slug);
  const location = useLocation();

  const tours = useMemo(() => toursQuery.data ?? [], [toursQuery.data]);
  // `after`: show only steps added after this version (an update); null shows the whole tour.
  const [active, setActive] = useState<{ key: string; index: number; after: number | null } | null>(null);
  const [queue, setQueue] = useState<string[]>([]);
  const [whatsNew, setWhatsNew] = useState<"unread" | "history" | null>(null);
  const [whatsNewHandled, setWhatsNewHandled] = useState(false);
  const [offerDismissed, setOfferDismissed] = useState<string | null>(null);
  const autoStarted = useRef(false);

  const activeTour = active ? tours.find((t) => t.key === active.key) : undefined;
  const activeSteps = useMemo(() => {
    if (!activeTour || !active) return [];
    const after = active.after;
    return after == null ? activeTour.steps : activeTour.steps.filter((step) => step.since > after);
  }, [activeTour, active]);

  const startTour = useCallback(
    (key: string, { onlyNew = false }: { onlyNew?: boolean } = {}) => {
      const tour = tours.find((t) => t.key === key);
      if (!tour) return;
      const after = onlyNew && newSteps(tour).length ? tour.seen_version : null;
      setWhatsNew(null);
      setActive({ key, index: 0, after });
      recordProgress({ key, action: "start" });
    },
    [tours, recordProgress],
  );

  const next = () => {
    setActive(null);
    const [head, ...rest] = queue;
    setQueue(rest);
    if (head) setTimeout(() => startTour(head), 250);
  };

  const finish = () => {
    if (activeTour) progress.mutate({ key: activeTour.key, action: "complete" });
    next();
  };
  const exit = () => {
    if (activeTour && activeTour.state !== "completed") progress.mutate({ key: activeTour.key, action: "dismiss" });
    setQueue([]);
    setActive(null);
  };

  // First run: auto-start shell tours the person has never seen, once. Later versions are offered.
  useEffect(() => {
    if (passive || autoStarted.current || active || !toursQuery.isSuccess) return;
    const auto = tours.find((t) => t.auto_start && t.state === "not_started");
    if (!auto) {
      autoStarted.current = true;
      return;
    }
    // Only mark as started when the tour actually starts: a re-render cancelling the timer must retry.
    const timer = setInterval(() => {
      if (dialogOpen()) return;
      clearInterval(timer);
      autoStarted.current = true;
      startTour(auto.key);
    }, 600);
    return () => clearInterval(timer);
  }, [tours, toursQuery.isSuccess, active, startTour, passive]);

  const autoPending = !passive && tours.some((t) => t.auto_start && t.state === "not_started");
  const unread = unreadQuery.data ?? [];

  // What's New after onboarding, once per session, only when nothing else is showing.
  useEffect(() => {
    if (passive || whatsNewHandled || active || autoPending || !unreadQuery.isSuccess || whatsNew) return;
    if (unread.length > 0) {
      const timer = setInterval(() => {
        if (dialogOpen()) return;
        clearInterval(timer);
        setWhatsNew("unread");
      }, 400);
      return () => clearInterval(timer);
    }
  }, [passive, whatsNewHandled, active, autoPending, unreadQuery.isSuccess, unread.length, whatsNew]);

  const closeWhatsNew = (tourKey?: string) => {
    // Everything unread was presented (both modes list it), so it is acknowledged now.
    const presented = unread.map((n) => n.key);
    if (presented.length) acknowledge.mutate(presented);
    setWhatsNew(null);
    setWhatsNewHandled(true);
    if (tourKey) setTimeout(() => startTour(tourKey), 200);
  };

  // Tour offer: a shell tour with new steps on any page, or a section's new/updated tour on its first
  // visit. Tours already started (taken or abandoned) aren't offered again; Profile replays them.
  const section = sectionFromPath(location.pathname);
  const offerable = (t: Tour) => t.eligible && t.state !== "started" && !offeredThisSession.has(`${slug}:${t.key}`);
  const offer =
    !passive && !active && !whatsNew && !autoPending
      ? (tours.find((t) => t.auto_start && offerable(t)) ??
        (section ? tours.find((t) => t.section === section && !t.auto_start && offerable(t)) : undefined))
      : undefined;
  const showOffer = offer && offerDismissed !== offer.key;
  const offerNew = offer ? newSteps(offer) : [];

  const takeOffer = (tour: Tour) => {
    offeredThisSession.add(`${slug}:${tour.key}`);
    startTour(tour.key, { onlyNew: true });
  };
  // "Not now" and closing both count as skipping this version: it isn't offered again until new steps arrive.
  const declineOffer = (tour: Tour) => {
    offeredThisSession.add(`${slug}:${tour.key}`);
    setOfferDismissed(tour.key);
    progress.mutate({ key: tour.key, action: "dismiss" });
  };

  const api = useMemo<GuidanceApi>(
    () => ({
      tours,
      activeTourKey: active?.key ?? null,
      startTour: (key) => startTour(key),
      replayAll: () => {
        const keys = tours.map((t) => t.key);
        if (!keys.length) return;
        setQueue(keys.slice(1));
        startTour(keys[0]);
      },
      openWhatsNew: () => setWhatsNew(unread.length ? "unread" : "history"),
    }),
    [tours, active, startTour, unread.length],
  );

  return (
    <GuidanceContext.Provider value={api}>
      {children}
      {activeTour && active && activeSteps.length > 0 && (
        <TourOverlay
          tour={activeTour}
          steps={activeSteps}
          index={active.index}
          onIndexChange={(index) => setActive({ ...active, index })}
          onFinish={finish}
          onExit={exit}
        />
      )}
      {whatsNew && <WhatsNewDialog slug={slug} mode={whatsNew} unread={unread} tours={tours} onClose={closeWhatsNew} />}
      {showOffer && offer && (
        <aside
          aria-label="Tour available"
          className="animate-pop-in fixed right-4 bottom-4 z-30 w-[min(340px,calc(100vw-2rem))] rounded-lg border border-line bg-surface-raised p-4 shadow-lg"
        >
          <div className="flex items-start gap-3">
            <span className="mt-0.5 rounded-md bg-accent-subtle p-1.5 text-accent-text">
              <Compass aria-hidden className="size-4" />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold text-fg">{offerNew.length ? `What's new in ${offer.title}` : `New to ${offer.title}?`}</h2>
              <p className="mt-0.5 text-sm text-fg-muted">
                {offerNew.length
                  ? `${offerNew.length} new ${offerNew.length === 1 ? "step" : "steps"} since you last looked.`
                  : `${offer.summary} ${offer.steps.length} quick steps.`}
              </p>
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="primary" onClick={() => takeOffer(offer)}>
                  {offerNew.length ? "Show me" : "Take the tour"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => declineOffer(offer)}>
                  Not now
                </Button>
              </div>
            </div>
            <button
              type="button"
              aria-label="Close tour offer"
              className="-m-1 flex size-6 items-center justify-center rounded-sm text-fg-subtle hover:text-fg"
              onClick={() => declineOffer(offer)}
            >
              <X aria-hidden className="size-4" />
            </button>
          </div>
        </aside>
      )}
    </GuidanceContext.Provider>
  );
}
